/**
 * Inbound mention normalization (spec §6.1): adapter-parsed platform mention
 * entities → the existing `Mention` union. This touches INBOUND platform
 * entities only — the platform's session-member mention grammar is untouched
 * (spec §12).
 */
import type { Mention } from "@skaile/workspaces/types";
import type { IdentityPort } from "../ports.js";
import type { ChannelActor } from "../types.js";

/** A platform mention entity as the adapter parsed it, pre-identity-resolution. */
export type ChannelMentionEntity = {
  externalId: string;
  displayName: string;
  /** True when the entity is the bot itself (adapter compares against its own id). */
  isBot: boolean;
  offset: number;
  length: number;
};

/**
 * Normalizes adapter-parsed mention entities into the existing Mention union
 * (spec §6.1): bot → { type:"group", group:"agent" }; mapped user → user;
 * unmapped → { type:"custom", kind:"external", data:{ platform, externalUserId } }.
 */
export async function normalizeMentions(
  entities: ChannelMentionEntity[],
  actorOf: (e: ChannelMentionEntity) => ChannelActor,
  identity: IdentityPort,
): Promise<Mention[]> {
  return Promise.all(
    entities.map(async (e): Promise<Mention> => {
      const position = { offset: e.offset, length: e.length };
      if (e.isBot) return { type: "group", group: "agent", resolved: [], ...position };
      const actor = actorOf(e);
      const host = await identity.resolveActor(actor);
      if (host) return { type: "user", userId: host.id, name: host.displayName, ...position };
      return {
        type: "custom",
        kind: "external",
        resolved: [],
        data: { platform: actor.platform, externalUserId: actor.externalId },
        ...position,
      };
    }),
  );
}

/** True when any normalized mention addresses the agent (bot-mention gate, spec §7.1.6). */
export function mentionsAgent(mentions: Mention[]): boolean {
  return mentions.some((m) => m.type === "group" && m.group === "agent");
}
